<template>
  <div>
    <nav class="cxm-TabBar" :class="[classes]">
      <div class="cxm-TabBar-Tabs" ref="tabs">
        <TabNavButton v-if="hasLeftNavigation && hideArrows === false" @click="handleNavigationLeft" />
        <div class="cxm-TabBar-Mask" ref="tabsmask">
          <ul class="cxm-TabBar-TabsList" ref="tabslist">
            <slot name="tabs" :actived="actived" :onclick="handleClickTab" />
          </ul>
        </div>
        <TabNavButton v-if="hasRightNavigation && hideArrows === false" @click="handleNavigationRight" right />
      </div>

      <div class="cxm-TabBar-icon cxm-TabBar-iconRight" v-if="$slots.icon">
        <slot name="icon" />
      </div>
    </nav>

    <TabContentManager :actived="actived">
      <slot name="content" :actived="actived" />
    </TabContentManager>
  </div>
</template>

<script>
import TabContentManager from './TabContentManager.vue';
import TabNavButton from './TabNavButton.vue';

export default {
  name: 't-tab-bar',
  components: {
    TabContentManager,
    TabNavButton,
  },
  data() {
    return {
      allTabSizes: [],
      countTabs: 0,
      countHiddenTabs: 0,
      hasRightNavigation: false,
      tabsMaskMarginLeft: 0,
      intersectionObserver: null,
    };
  },
  model: {
    prop: 'actived',
    event: 'change',
  },
  props: {
    actived: { type: String, default: '' },
    flat: { type: Boolean, default: false },
    hideArrows: { type: Boolean, default: false },
    navigation: { type: Boolean, default: false },
  },
  emits: ['change'],
  computed: {
    classes() {
      return {
        'is-flat': this.flat,
      };
    },
    hasLeftNavigation() {
      return this.countHiddenTabs > 0;
    },
  },
  methods: {
    calcTabsSize() {
      const $tabsMask = this.$refs.tabsmask;
      const $tabs = $tabsMask.getElementsByTagName('li');
      this.countTabs = $tabs.length;

      for (const tab in $tabs) {
        if (Object.hasOwnProperty.call($tabs, tab)) {
          const tabWidth = $tabs[tab].offsetWidth;
          this.allTabSizes.push(tabWidth);
        }
      }
    },
    createMutationObserver() {
      const $tabslist = this.$refs.tabslist;
      const options = { attributes: false, childList: true, subtree: false };
      const observer = new MutationObserver(() => {
        if (this.intersectionObserver) {
          this.intersectionObserver.disconnect();
        }

        this.createIntersectionObserver();
        this.calcTabsSize();
      });
      observer.observe($tabslist, options);
    },
    createIntersectionObserver() {
      const $tabs = this.$refs.tabs;
      const $tabsMask = this.$refs.tabsmask;
      const $lastTab = $tabsMask.querySelector('li:last-child');

      const callback = (entries) => {
        entries.forEach(({ intersectionRatio }) => {
          this.hasRightNavigation = intersectionRatio < 1;
        });
      };

      const options = {
        root: $tabs,
        rootMargin: '0px',
        threshold: [1],
      };

      this.intersectionObserver = new IntersectionObserver(callback, options);
      this.intersectionObserver.observe($lastTab);
    },
    handleClickTab(tabIndex) {
      this.$emit('change', tabIndex);
    },
    handleNavigationLeft() {
      if (this.hasLeftNavigation && this.countHiddenTabs > 0) {
        const $tabsMask = this.$refs.tabsmask;
        this.countHiddenTabs -= 1;
        this.tabsMaskMarginLeft += this.allTabSizes[this.countHiddenTabs];
        $tabsMask.style.marginLeft = `${this.tabsMaskMarginLeft}px`;
      }
    },
    handleNavigationRight() {
      if (this.hasRightNavigation && this.countHiddenTabs < this.countTabs) {
        const $tabsMask = this.$refs.tabsmask;
        this.tabsMaskMarginLeft -= this.allTabSizes[this.countHiddenTabs];
        $tabsMask.style.marginLeft = `${this.tabsMaskMarginLeft}px`;
        this.countHiddenTabs += 1;
      }
    },
  },
  mounted() {
    this.calcTabsSize();
    this.createIntersectionObserver();
    this.createMutationObserver();
  },
};
</script>

<style lang="scss" scoped>
.cxm-TabBar {
  align-items: center;
  background-color: white;
  border-top-left-radius: var(--track-border-radius-lg);
  border-top-right-radius: var(--track-border-radius-lg);
  border-bottom: 1px solid var(--track-color-bluegray-20);
  display: flex;
  overflow: hidden;

  &.is-flat {
    border-radius: 0;
  }
}

.cxm-TabBar-Tabs {
  flex: 1;
  overflow: hidden;
  position: relative;

  &>button {
    opacity: 0;
  }

  &:hover>button {
    opacity: 1;
  }
}

.cxm-TabBar-Mask {
  width: 10000px;
  transition: all 0.2s;
}

.cxm-TabBar-TabsList {
  margin: 0;
  padding: 0;
}

.cxm-TabBar-icon {
  padding: 0 var(--track-spacing-md);

  &.cxm-TabBar-iconRight {
    margin: 0 0 0 auto;
  }
}
</style>
